import { useState, useEffect } from "react";
import { Bell, BellRinging, PaperPlaneTilt } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { pushSupported, currentSubscription, enablePush, disablePush, sendTestPush } from "@/lib/push";

export default function NotificationBell() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();

  useEffect(() => {
    if (!supported) return;
    currentSubscription().then((s) => setEnabled(!!s)).catch(() => {});
  }, [supported]);

  const toggle = async () => {
    setBusy(true);
    try {
      if (enabled) {
        await disablePush();
        setEnabled(false);
        toast.success("Coach nudges turned off");
      } else {
        await enablePush();
        setEnabled(true);
        toast.success("Coach nudges on — I'll reach you at the right moments 🌿");
      }
    } catch (e) {
      toast.error(e.message || "Could not update notifications");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      await sendTestPush();
      toast.success("Test nudge sent — check your notifications");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enable notifications first");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell-btn"
          className="w-9 h-9 rounded-full bg-surface border border-border flex items-center justify-center hover:bg-secondary transition-colors relative"
          title="Coach notifications"
        >
          {enabled ? <BellRinging size={17} weight="fill" color="var(--brand)" /> : <Bell size={17} weight="regular" color="#7C8AA5" />}
          {enabled && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-sage border border-white" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 rounded-2xl p-5" data-testid="notifications-popover">
        <div className="label-eyebrow">Proactive coaching</div>
        <p className="text-sm text-muted2 mt-2 leading-relaxed">
          Get a gentle push when your recovery dips, your streak is at risk, or it's time to wind down for sleep.
        </p>
        {!supported ? (
          <p className="text-xs text-destructive mt-4">This browser doesn't support push notifications.</p>
        ) : (
          <div className="mt-4 space-y-2">
            <button
              data-testid="notifications-toggle-btn"
              onClick={toggle}
              disabled={busy}
              className={`w-full rounded-full py-2.5 text-sm font-medium transition-colors disabled:opacity-60 ${
                enabled ? "bg-secondary hover:bg-border text-ink" : "bg-brand text-white hover:bg-ink"
              }`}
            >
              {enabled ? "Turn off nudges" : "Enable nudges"}
            </button>
            {enabled && (
              <button
                data-testid="notifications-test-btn"
                onClick={test}
                disabled={busy}
                className="w-full rounded-full py-2.5 text-sm font-medium border border-border hover:bg-secondary transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <PaperPlaneTilt size={15} weight="fill" color="var(--brand)" />
                Send me a test
              </button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
